import React, { useEffect, useState } from 'react';
import { FaRobot, FaSearch, FaPhoneAlt, FaWhatsapp, FaChevronLeft, FaChevronRight, FaRegClock } from 'react-icons/fa';
import BaseUrl from '../../ApiServices/BaseUrl';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import './AdminLeadBotLeads.css';

const statusBadgeClass = {
  new: 'status-new',
  contacted: 'status-contacted',
  closed: 'status-closed'
};

const AdminLeadBotLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);
  const itemsPerPage = 10;

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      // Note: Assuming there's a token for admin auth in Gulfworker as well
      
      const offset = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (statusFilter) params.set('status', statusFilter);
      params.set('offset', offset.toString());
      params.set('pageSize', itemsPerPage.toString());

      const response = await fetch(`${BaseUrl}/api/lead-bot/leads?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setLeads(result.data);
        setTotalItems(result.pagination.total);
      } else {
        throw new Error(result.message || 'Failed to fetch leads');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [currentPage, searchQuery, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setUpdatingId(id);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BaseUrl}/api/lead-bot/leads/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const result = await response.json();
      if (result.success) {
        setLeads(leads.map(lead => lead.id === id ? { ...lead, status: newStatus } : lead));
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating status');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const newCount = leads.filter((lead) => lead.status === 'new').length;
  const contactedCount = leads.filter((lead) => lead.status === 'contacted').length;
  const closedCount = leads.filter((lead) => lead.status === 'closed').length;

  return (
    <>
      <AdminNavbar />
      <div className="admin-leads-page">
        <div className="admin-leads-container">
          <div className="admin-leads-header">
            <div className="header-copy">
              <div className="header-title">
                <div className="header-icon-shell">
                  <FaRobot className="header-icon" />
                </div>
                <div>
                  <span className="header-eyebrow">Admin Enquiries</span>
                  <h1>Lead Bot Submissions</h1>
                </div>
              </div>
              <p className="header-subtitle">Manage inquiries captured by the Gulf Worker chatbot, monitor follow-up status, and reach leads directly from one workspace.</p>
            </div>

            <div className="header-stats" aria-label="Lead summary">
              <div className="summary-tile">
                <span className="summary-label">Visible Leads</span>
                <strong>{loading ? '--' : leads.length}</strong>
              </div>
              <div className="summary-tile">
                <span className="summary-label">New</span>
                <strong>{loading ? '--' : newCount}</strong>
              </div>
              <div className="summary-tile">
                <span className="summary-label">Contacted</span>
                <strong>{loading ? '--' : contactedCount}</strong>
              </div>
              <div className="summary-tile">
                <span className="summary-label">Closed</span>
                <strong>{loading ? '--' : closedCount}</strong>
              </div>
            </div>
          </div>

          <div className="admin-leads-toolbar">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or category..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <button type="submit" className="search-btn">Search</button>
            </form>

            <div className="status-filter">
              <label htmlFor="lead-status-filter">Filter Status</label>
              <select
                id="lead-status-filter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="leads-panel">
            <div className="panel-heading">
              <div>
                <h2>Lead queue</h2>
                <p>Review submissions, update progress, and start follow-ups without leaving the page.</p>
              </div>
              <div className="panel-meta">
                <span>{loading ? 'Loading...' : `${totalItems} total leads`}</span>
                <span>{`Page ${currentPage} of ${totalPages}`}</span>
              </div>
            </div>

            <div className="leads-table-wrapper">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Lead Info</th>
                    <th>Worker Type</th>
                    <th>Mobile Number</th>
                    <th>Requirement</th>
                    <th>Submitted</th>
                    <th>Assignment</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="table-loading">Loading leads...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="9" className="table-empty">Unable to load leads. {error}</td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="table-empty">No leads found matching your criteria.</td>
                    </tr>
                  ) : (
                    leads.map((lead, index) => (
                      <tr key={lead.id}>
                        <td data-label="Sr No">
                          <div className="serial-cell">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </div>
                        </td>
                        <td data-label="Lead Info">
                          <div className="lead-info-cell">
                            <div className="lead-name">{lead.name}</div>
                          </div>
                        </td>
                        <td data-label="Worker Type">
                          <div className="worker-type-cell">
                            <div className="category-tag">{lead.category}{lead.other_category_text ? ` (${lead.other_category_text})` : ''}</div>
                          </div>
                        </td>
                        <td data-label="Mobile Number">
                          <div className="lead-phone">{lead.phone_country_code} {lead.phone}</div>
                        </td>
                        <td data-label="Requirement">
                          <div className="requirement-cell">
                            <div className="sub-detail">{lead.nationality} ({lead.work_type})</div>
                            <div className="sub-detail">{lead.living_preference} | {lead.timeline}</div>
                          </div>
                        </td>
                        <td data-label="Submitted">
                          <div className="date-cell">
                            <FaRegClock />
                            <span>{formatDate(lead.created_at)}</span>
                          </div>
                        </td>
                        <td data-label="Assignment">
                          <div className="assignment-cell">
                            {lead.assigned_to_name ? (
                              <>
                                <div className="assigned-name">{lead.assigned_to_name}</div>
                                <div className="assigned-type">{lead.assigned_to_type}</div>
                              </>
                            ) : (
                              <span className="unassigned">Unassigned</span>
                            )}
                          </div>
                        </td>
                        <td data-label="Status">
                          <select
                            className={`status-select ${statusBadgeClass[lead.status]}`}
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                            disabled={updatingId === lead.id}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                        <td data-label="Actions">
                          <div className="action-btns">
                            <a
                              href={`https://wa.me/${(lead.phone_country_code || '').replace('+','') + lead.phone}`}
                              className="wa-action-btn"
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Chat on WhatsApp"
                            >
                              <FaWhatsapp />
                            </a>
                            <a
                              href={`tel:${lead.phone_country_code}${lead.phone}`}
                              className="call-action-btn"
                              title="Call Lead"
                            >
                              <FaPhoneAlt />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">
                Showing {leads.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} leads
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1 || loading}
                  className="pager-btn"
                >
                  <FaChevronLeft /> Prev
                </button>
                <span className="page-num">Page {currentPage}</span>
                <button
                  onClick={() => setCurrentPage(prev => (prev * itemsPerPage < totalItems ? prev + 1 : prev))}
                  disabled={currentPage * itemsPerPage >= totalItems || loading}
                  className="pager-btn"
                >
                  Next <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLeadBotLeads;
